import { AddonCard } from 'components/AddonsMode/AddonCard/AddonCard';
import { PetChip } from 'components/Pet/Chip/PetChip';
import React from 'react';
import { AddonSchedule } from 'types/AddonSchedule';
import { AddonService } from 'types/AddonService';
import { PortalBooking } from 'types/PortalBooking';
import { getAddonDates } from 'utils/addonSchedule';
import { AddonGroup, AddonSelections, getLockedDays, getSelectionKey } from 'utils/addonsMode';

import { SummaryPet } from './Summary/AddonsSummary';
import classes from './AddonsMode.module.scss';

interface AddonsModePetStepProps {
    booking: PortalBooking;
    pets: Array<SummaryPet>;
    petId: number;
    completedPetIds: Array<number>;
    groups: Array<AddonGroup>;
    selections: AddonSelections;
    showScheduleErrors: boolean;
    notice?: React.ReactNode;
    onSelectPet: (petId: number) => void;
    onToggle: (group: AddonGroup, addon: AddonService, checked: boolean) => void;
    onScheduleChange: (group: AddonGroup, addon: AddonService, schedule: AddonSchedule) => void;
}

/** One pet at a time: chips, then its Add-Ons grouped by service (Figma 1930:6145 / 1930:6228). */
export const AddonsModePetStep: React.FC<AddonsModePetStepProps> = ({
    booking,
    pets,
    petId,
    completedPetIds,
    groups,
    selections,
    showScheduleErrors,
    notice,
    onSelectPet,
    onToggle,
    onScheduleChange
}) => {
    const pet = pets.find(candidate => candidate.id === petId);

    return (
        <>
            <div className={classes.chips}>
                {pets.map(candidate => (
                    <PetChip
                        key={candidate.id}
                        petName={candidate.name}
                        petType={candidate.type}
                        active={candidate.id === petId}
                        checked={completedPetIds.includes(candidate.id)}
                        onClick={() => onSelectPet(candidate.id)}
                    />
                ))}
            </div>

            {notice}

            {groups.length === 0 ? (
                <div className={classes.empty}>
                    <p className={classes.emptyTitle}>No Add-Ons available for {pet?.name}</p>
                    <p className={classes.emptyText}>
                        There are no Add-Ons you can add online to {pet?.name}’s services on this
                        booking.
                    </p>
                </div>
            ) : (
                <div className={classes.groups}>
                    {groups.map(group => (
                        <section key={group.key} className={classes.group} aria-label={group.label}>
                            <h2 className={classes.groupTitle}>{group.label}</h2>

                            <div className={classes.cards}>
                                {group.addons.map(addon => (
                                    <AddonCard
                                        key={addon.id}
                                        addon={addon}
                                        eligibleDates={getAddonDates(addon, group.appointments)}
                                        locked={getLockedDays(booking, petId, addon.id)}
                                        saved={booking.addons.filter(
                                            line =>
                                                line.pet.id === petId &&
                                                line.addonServiceId === addon.id
                                        )}
                                        schedule={
                                            selections[petId]?.[
                                                getSelectionKey(group.key, addon.id)
                                            ]
                                        }
                                        showScheduleError={showScheduleErrors}
                                        onToggle={checked => onToggle(group, addon, checked)}
                                        onScheduleChange={schedule =>
                                            onScheduleChange(group, addon, schedule)
                                        }
                                    />
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            )}
        </>
    );
};
